import { Editor, ReactEditor, NodeEntry } from '../../core';
import { usePlugins } from '../../hooks';
import { useCallback } from 'react';
import { PICKLIST_PLUGIN_ID, insertSelectedPicklistItem } from '../picklist';
import { createEditorWarning, partialEditor } from '../../utils';
import { usePicklistEditor } from '../../../Template/usePicklist';
import { unfurlMacroPlaceholderStatic } from '../macroPlaceholder';
import analytics from 'modules/analytics';
import { reporter } from 'modules/analytics/constants';
import { logger } from 'modules/logger';
import { stringifyRange } from '../../utils/stringify';
import { useRequiredFieldIndicator } from 'hooks/useRequiredFieldIndicator';
import { Element, Node } from 'slate';
import { PicklistPluginElement } from '../../slate-custom-types';
import { getActivePicklist } from '../../utils/selection';
import { isSelectionContainedInNode } from '../../utils/isSelectionContainedInNode';
import type { PicklistOption } from '../../../Template/usePicklist';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { PICKLIST_COMMAND_ALIASES, PICKLIST_COMMAND_WORDS } from './NvoqQueue/utils/commandUtils';
import {
  normalizeTextToWords,
  normalizeOptionNameForMatching,
  parsePickIndexFromOptionNameMatchAnywhere,
  isExplicitPicklistTriggerWord,
  SPOKEN_NUMBER_WORDS_MAP,
  getPreAndPostTextFromWordRange,
  getMatchedRawWordLengthFromNormalizedWords,
  isFullInitialTriggerlessMatch,
} from '../picklist/utils';
import {
  resolveBestFuzzyPicklistOptionMatch,
  GetPicklistOptionSelectionFuzzyMatch,
  GetPicklistOptionSelectionMatchOptions,
} from './picklistSelection/picklistSelectionDecision';
import { parsePotentialPicklistCommand } from './picklistSelection/parsePotentialPicklistCommand';
export {
  PicklistSelectionDecisionRule,
  type PicklistOptionSelectionFuzzyMatch,
  PicklistSelectionMatchType,
} from './picklistSelection/picklistSelectionDecision';
export {
  getAdvisorCommandStableTextPrefixMatch,
  type PicklistAdvisorCommandStableTextPrefixMatch,
} from './picklistSelection/advisorCommandPrefixMatch';
export type {
  GetPicklistOptionSelectionFuzzyMatch,
  GetPicklistOptionSelectionMatchOptions,
} from './picklistSelection/picklistSelectionDecision';
export { parsePotentialPicklistCommand };
export type { ParsePotentialPicklistCommandResult } from './picklistSelection/parsePotentialPicklistCommand';
export enum PicklistSelectionSource {
  StableText = 'stable_text',
  WebSpeechAdvisor = 'webspeech_advisor',
}

/**
 * Returns a number if it finds one in the string that should be number. Otherwise
 * returns -1. Supports both digit strings ("1", "4") and spoken words ("one", "for", "four").
 */
export const parsePicklistNumber = (numberString?: string | null): number => {
  if (numberString == null) return -1;

  const trimmed = numberString.trim();
  if (trimmed === '') return -1;

  const potentialNumber = parseInt(trimmed, 10);
  if (!Number.isNaN(potentialNumber)) {
    return potentialNumber;
  }

  return SPOKEN_NUMBER_WORDS_MAP[trimmed.toLowerCase()] ?? -1;
};
export type GetPicklistOptionSelectionMatchReturn = {
  preMatchText: string;
  pickIndex: number;
  postMatchText: string;
};

export type GetPicklistOptionSelectionMatch = (
  text: string,
  options?: GetPicklistOptionSelectionMatchOptions
) => Nullable<GetPicklistOptionSelectionMatchReturn>;

export type InsertSelectedPicklistItemByIndexOptions = {
  /** optional picklist id from dictation registration, otherwise uses current active picklist */
  picklistId?: string | null;
};

export type InsertSelectedPicklistItemByIndexItem = (
  pickIndex: number,
  options?: InsertSelectedPicklistItemByIndexOptions
) => string | null;

export type UsePicklistDictationSelectionBag = Readonly<{
  insertSelectedPicklistItemByIndex: InsertSelectedPicklistItemByIndexItem;
  getPicklistOptionSelectionMatch: GetPicklistOptionSelectionMatch;
  getPicklistOptionSelectionFuzzyMatch: GetPicklistOptionSelectionFuzzyMatch;
}>;

type OptionMatch = {
  pickIndex: number;
  matchedWordLength: number;
  matchedRawWordLength?: number;
  matchStartWordIndex?: number;
  matchStartRawWordIndex?: number;
};

const isSpokenNumberWord = (word?: string | null): boolean => {
  if (word == null) return false;
  const trimmed = word.trim();
  if (trimmed === '') return false;
  // If it's a digit string, treat as regular numeric command (always allowed)
  if (/^\d+$/.test(trimmed)) return false;
  return SPOKEN_NUMBER_WORDS_MAP[trimmed.toLowerCase()] != null;
};

const isAliasCommandWord = (word: string): boolean => {
  const lower = word.toLowerCase();
  return (PICKLIST_COMMAND_ALIASES as readonly string[]).some(
    (alias) => alias.toLowerCase() === lower
  );
};

// Determine if this dictation should be treated as having picklist context:
// - explicit dictation picklistId, or
// - current selection is inside a picklist.
const hasPicklistContextForMatch = (
  editor: Nullable<Editor>,
  options?: GetPicklistOptionSelectionMatchOptions
): boolean => {
  if (options?.picklistId != null) return true;
  if (editor == null || editor.selection == null) return false;

  const picklistAtSelection = getActivePicklist(
    editor,
    editor.selection
  ) as NodeEntry<PicklistPluginElement> | null;

  return picklistAtSelection != null;
};

const parsePickIndexFromNumericCommand = (
  words: string[],
  commandWordIndex: number
): OptionMatch | null => {
  // Supports "<trigger word> <option index>" such as "pick 2".
  const pickIndexFromNextWord = parsePicklistNumber(words[commandWordIndex + 1]) - 1;

  if (pickIndexFromNextWord >= 0) {
    return { pickIndex: pickIndexFromNextWord, matchedWordLength: 1 };
  }

  return null;
};

const parsePickIndexFromOptionNameMatch = ({
  wordsAfterCommand,
  options,
}: {
  wordsAfterCommand: string[];
  options: ReadonlyArray<PicklistOption>;
}): OptionMatch | null => {
  // Normalize dictated text the same way as choice names so nVoq output "comet-tail" (from
  // "comet hyphen tail" / "comet dash tail") matches choice name "comet-tail" or "comet tail".
  const normalizedWordsAfterCommand = normalizeTextToWords(
    normalizeOptionNameForMatching(wordsAfterCommand.join(' '))
  );

  const optionVariants = options
    .map((option) => ({
      pickIndex: option.pickIndex,
      words: normalizeTextToWords(
        normalizeOptionNameForMatching(typeof option.name === 'string' ? option.name : '')
      ),
    }))
    .filter((option) => option.words.length > 0);

  const matchedOption = optionVariants
    // Prefer the longest option phrase to avoid partial shorter matches.
    .sort((a, b) => b.words.length - a.words.length)
    .find((option) =>
      option.words.every((word, index) => normalizedWordsAfterCommand[index] === word)
    );

  if (matchedOption == null) {
    return null;
  }

  return {
    pickIndex: matchedOption.pickIndex,
    matchedWordLength: matchedOption.words.length,
    matchedRawWordLength: getMatchedRawWordLengthFromNormalizedWords({
      rawWords: wordsAfterCommand,
      matchedNormalizedWordLength: matchedOption.words.length,
    }),
  };
};

export const usePicklistDictationSelection = ({
  editor,
}: {
  editor: Nullable<Editor>;
}): UsePicklistDictationSelectionBag => {
  const { activatedPlugins, getEditorStateEnhancers } = usePlugins();

  const { picklists, macros, setSelectedPicklistOptionIds, activePicklist, getPicklistOptions } =
    usePicklistEditor();
  const [isReporterPicklistOptionByNameEnabled] = useFeatureFlagEnabled(
    FF.REPORTER_PICKLIST_OPTION_BY_NAME
  );

  const { onInsertTextRequiredField } = useRequiredFieldIndicator();

  const getPicklistOptionSelectionMatchCallback = useCallback(
    (text: string, options?: GetPicklistOptionSelectionMatchOptions) => {
      if (text === '') return null;

      if (!activatedPlugins.includes(PICKLIST_PLUGIN_ID)) {
        throw new Error(
          `Cannot use the dictation plugin without using the ${PICKLIST_PLUGIN_ID} plugin.`
        );
      }

      // Normalize once up front so fuzzy scoring sees a stable token stream.
      const normalizedText = text.trim().toLowerCase();
      const words = normalizedText.split(/\s+/).filter(Boolean);

      const picklistCommand = parsePotentialPicklistCommand(words);

      const picklistIdForMatch = options?.picklistId ?? activePicklist?.id ?? null;

      // Trigger-less matching is opt-in only when callers provide a non-null picklistId.
      // Trigger-less fuzzy follows the same non-null picklistId gate.
      const allowTriggerlessOptionNameMatch =
        options?.allowTriggerlessOptionNameMatch ?? options?.picklistId != null;
      const picklistOptions = getPicklistOptions(picklistIdForMatch);

      logger.info('[picklist dictation] getPicklistOptionSelectionMatch', {
        optionsPicklistId: options?.picklistId,
        activePicklistId: activePicklist?.id,
        picklistIdForMatch,
      });

      if (picklistCommand != null) {
        const hasPicklistContext = hasPicklistContextForMatch(editor, options);
        const commandWordIndex = picklistCommand.wordIndex;
        const nextWord = words[commandWordIndex + 1];
        const isAliasCommand = isAliasCommandWord(picklistCommand.dictatedCommand);

        // gate edge cases that only work in picklist context
        if ((isAliasCommand || isSpokenNumberWord(nextWord)) && !hasPicklistContext) {
          return null;
        }

        const numericMatch = parsePickIndexFromNumericCommand(words, picklistCommand.wordIndex);
        const optionNameMatch = isReporterPicklistOptionByNameEnabled
          ? parsePickIndexFromOptionNameMatch({
              wordsAfterCommand: words.slice(picklistCommand.wordIndex + 1),
              options: picklistOptions,
            })
          : null;

        // Option-name phrase matching takes precedence over numeric-only matching.
        const selectionMatch = optionNameMatch ?? numericMatch;

        // If we have a trigger word and a match for an option, return the match with pre/post text for insertion.
        if (selectionMatch != null) {
          const { pickIndex, matchedWordLength, matchedRawWordLength } = selectionMatch;
          const { preMatchText, postMatchText } = getPreAndPostTextFromWordRange({
            text,
            matchStartWordIndex: picklistCommand.wordIndex,
            matchEndWordIndexInclusive:
              picklistCommand.wordIndex + (matchedRawWordLength ?? matchedWordLength),
          });

          return {
            preMatchText,
            pickIndex,
            postMatchText,
          };
        }

        // If a trigger-like word appears later in free text (e.g. "second option"), fall through
        // to trigger-less matching using the full dictated text.
        if (
          picklistCommand.wordIndex === 0 &&
          isExplicitPicklistTriggerWord({
            word: picklistCommand.dictatedCommand,
            commandWords: PICKLIST_COMMAND_WORDS,
            commandAliases: PICKLIST_COMMAND_ALIASES,
          })
        ) {
          return null;
        }
      }

      // Trigger-less mode: only by option name, and only when we have explicit picklist context.
      if (
        !isReporterPicklistOptionByNameEnabled ||
        !allowTriggerlessOptionNameMatch ||
        picklistIdForMatch == null
      ) {
        return null;
      }

      const optionNameOnlyMatch = parsePickIndexFromOptionNameMatch({
        wordsAfterCommand: words,
        options: picklistOptions,
      });
      const optionNameOnlyMatchAnywhere =
        optionNameOnlyMatch ??
        parsePickIndexFromOptionNameMatchAnywhere({
          words,
          options: picklistOptions,
        });

      if (optionNameOnlyMatchAnywhere == null) {
        return null;
      }

      const startIndex =
        optionNameOnlyMatchAnywhere.matchStartRawWordIndex ??
        optionNameOnlyMatchAnywhere.matchStartWordIndex ??
        0;
      const pickIndex = optionNameOnlyMatchAnywhere.pickIndex;
      const { preMatchText, postMatchText } = getPreAndPostTextFromWordRange({
        text,
        matchStartWordIndex: startIndex,
        matchEndWordIndexInclusive:
          startIndex +
          (optionNameOnlyMatchAnywhere.matchedRawWordLength ??
            optionNameOnlyMatchAnywhere.matchedWordLength) -
          1,
      });

      // Triggerless option-name selection should only fire when stable text is purely the
      // option phrase (no surrounding dictated text in the same chunk).
      if (
        !isFullInitialTriggerlessMatch({
          preMatchText,
          postMatchText,
        })
      ) {
        return null;
      }

      return {
        preMatchText,
        pickIndex,
        postMatchText,
      };
    },
    [
      activatedPlugins,
      editor,
      getPicklistOptions,
      activePicklist?.id,
      isReporterPicklistOptionByNameEnabled,
    ]
  );

  const getPicklistOptionSelectionFuzzyMatchCallback = useCallback(
    (text: string, options?: GetPicklistOptionSelectionMatchOptions) => {
      if (text.trim() === '') return null;
      if (!isReporterPicklistOptionByNameEnabled) return null;

      const normalizedText = text.trim().toLowerCase();
      const words = normalizedText.split(/\s+/).filter(Boolean);

      const picklistCommand = parsePotentialPicklistCommand(words);
      const picklistIdForMatch = options?.picklistId ?? activePicklist?.id ?? null;
      const allowTriggerlessOptionNameMatch =
        options?.allowTriggerlessOptionNameMatch ?? options?.picklistId != null;
      const picklistOptions = getPicklistOptions(picklistIdForMatch);

      if (picklistCommand != null) {
        // Trigger-word mode: fuzzy-match only the phrase after the command token.
        const wordsAfterCommand = words.slice(picklistCommand.wordIndex + 1);
        const best = resolveBestFuzzyPicklistOptionMatch({
          phraseWords: wordsAfterCommand,
          picklistOptions,
        });
        if (best == null) return null;

        return {
          preMatchText: words.slice(0, picklistCommand.wordIndex).join(' '),
          pickIndex: best.pickIndex,
          postMatchText: words
            .slice(picklistCommand.wordIndex + 1 + best.matchedWordLength)
            .join(' '),
          confidence: best.confidence,
          margin: best.margin,
        };
      }

      // Trigger-less fuzzy mode is opt-in only when explicit picklist context is provided.
      if (!allowTriggerlessOptionNameMatch || picklistIdForMatch == null) {
        return null;
      }

      // Trigger-less mode can start at any position (e.g. "foo white night bar") where 'white knight' is the picklist option name.
      const bestTriggerlessMatch = resolveBestFuzzyPicklistOptionMatch({
        phraseWords: words,
        picklistOptions,
        allowStartAnywhere: true,
      });
      if (bestTriggerlessMatch == null) {
        return null;
      }

      const preMatchText = words.slice(0, bestTriggerlessMatch.matchStartWordIndex).join(' ');
      const postMatchText = words
        .slice(bestTriggerlessMatch.matchStartWordIndex + bestTriggerlessMatch.matchedWordLength)
        .join(' ');

      if (
        !isFullInitialTriggerlessMatch({
          preMatchText,
          postMatchText,
        })
      ) {
        return null;
      }

      return {
        preMatchText,
        pickIndex: bestTriggerlessMatch.pickIndex,
        postMatchText,
        confidence: bestTriggerlessMatch.confidence,
        margin: bestTriggerlessMatch.margin,
      };
    },
    [activePicklist?.id, getPicklistOptions, isReporterPicklistOptionByNameEnabled]
  );

  const insertSelectedPicklistItemCallback = useCallback(
    (pickIndex: number, options?: InsertSelectedPicklistItemByIndexOptions) => {
      if (editor == null) {
        return null;
      }
      // when set, comes from step payload (processStableText → createStepsFromStableText → step → maybeDoVoiceCommand).
      // when omitted, no picklist context was passed (user wasn't in a picklist at registration or processing); fall back to picklist at selection
      const targetPicklistId = options?.picklistId;
      const selection = editor.selection ?? null;

      let picklistEntry: NodeEntry<PicklistPluginElement> | null = null;

      // Dictation context: use the registered/target picklist. If selection is in it, use it; else find it in the document (e.g. user tabbed out).
      if (targetPicklistId != null) {
        const picklistAtSelection =
          selection != null
            ? (getActivePicklist(editor, selection) as NodeEntry<PicklistPluginElement> | null)
            : null;
        const picklistNodeAtSelection = picklistAtSelection?.[0];

        if (
          picklistNodeAtSelection != null &&
          (picklistNodeAtSelection as PicklistPluginElement).picklistID === targetPicklistId
        ) {
          picklistEntry = picklistAtSelection;
        }
        // User tabbed out: find the dictation's picklist in the document and insert there.
        else {
          const targetEntry = Editor.nodes(editor, {
            at: [],
            match: (n) =>
              Element.isElement(n) &&
              n.type === PICKLIST_PLUGIN_ID &&
              (n as PicklistPluginElement).picklistID === targetPicklistId,
          }).next().value as NodeEntry<PicklistPluginElement> | undefined;
          if (targetEntry != null) {
            picklistEntry = targetEntry;
          }
        }
      }
      // No dictation context: use picklist at current selection and get active picklist to match
      else if (selection != null) {
        picklistEntry = getActivePicklist(
          editor,
          selection
        ) as NodeEntry<PicklistPluginElement> | null;
        if (
          picklistEntry != null &&
          (picklistEntry[0] as PicklistPluginElement).picklistID !== activePicklist?.id
        ) {
          return null;
        }
      }

      if (picklistEntry == null) {
        logger.info(
          '[picklist dictation] insertSelectedPicklistItemByIndex bail: no picklist (selection or target not in picklist)',
          {
            selectionRange: selection != null ? stringifyRange(selection) : null,
            targetPicklistId,
          }
        );
        return null;
      }

      const [picklistNode] = picklistEntry;
      const shouldPreserveCurrentSelection =
        targetPicklistId != null &&
        selection != null &&
        !isSelectionContainedInNode(editor, picklistEntry[1], selection);

      const foundPicklist = picklists.find((picklist) => picklist.id === picklistNode.picklistID);

      if (!foundPicklist) {
        const missingPicklistError = `[usePicklistDictationSelection] Could not find a picklist from the picklist ID ${picklistNode.picklistID}. Skipping picklist shortcut.`;
        createEditorWarning(missingPicklistError);

        logger.error(missingPicklistError, {
          editor: editor != null ? partialEditor(editor) : 'null',
          picklistID: picklistNode.picklistID,
        });
        return null;
      }

      const optionToInsert = foundPicklist.options?.[pickIndex];
      if (optionToInsert == null) return null;

      analytics.track(reporter.usr.voiceCommand, {
        commandType: 'InsertPicklist',
        selection: editor.selection != null ? stringifyRange(editor.selection) : 'null',
      });

      insertSelectedPicklistItem(
        editor,
        picklistEntry as NodeEntry<PicklistPluginElement & { children: Node[] }>,
        unfurlMacroPlaceholderStatic(optionToInsert.text, macros, getEditorStateEnhancers(), {
          fragmentMode: true,
        }),
        () => onInsertTextRequiredField(ReactEditor.findKey(editor, picklistNode)),
        {
          selectInsertedContent: !shouldPreserveCurrentSelection,
        }
      );

      setSelectedPicklistOptionIds((prev) => ({
        ...prev,
        [foundPicklist.id]: optionToInsert.id,
      }));

      return picklistNode.picklistID ?? foundPicklist.id;
    },
    [
      activePicklist?.id,
      editor,
      getEditorStateEnhancers,
      macros,
      onInsertTextRequiredField,
      picklists,
      setSelectedPicklistOptionIds,
    ]
  );

  return {
    getPicklistOptionSelectionMatch: getPicklistOptionSelectionMatchCallback,
    getPicklistOptionSelectionFuzzyMatch: getPicklistOptionSelectionFuzzyMatchCallback,
    insertSelectedPicklistItemByIndex: insertSelectedPicklistItemCallback,
  };
};
